import { useCallback, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Badge from "@/components/ui/Badge";
import type { MapPin } from "@/interfaces/satellite_event";

// Leaflet's default marker is a PNG from its own dist path and looks nothing
// like the rest of the platform, so every pin is a divIcon built from theme
// tokens. Applications and live events read differently because the question HQ
// asks the map is "is this new one next to a city we already run".
const PIN_TONE: Record<string, string> = {
  submitted: "bg-carrot-orange",
  approved: "bg-leaf",
};

// How close two pins have to be on screen before they're drawn as one. Roughly
// the width of a pin, so markers never sit on top of each other.
const MERGE_PX = 26;

function pinIcon(pins: MapPin[]) {
  // A merged pin covering both an application and a live event should read as
  // the live one - that a city is already running is the stronger signal.
  const state = pins.some((p) => p.state === "approved")
    ? "approved"
    : pins[0].state;
  const tone = PIN_TONE[state] ?? "bg-viking";

  return L.divIcon({
    className: "",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    html: `<span class="border-line ${tone} font-display text-caption flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 font-bold text-white shadow-[0_2px_0_var(--color-line)]">${pins.length > 1 ? pins.length : ""}</span>`,
  });
}

function escapeHtml(value: string) {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] ?? c,
  );
}

function popupHtml(pins: MapPin[]) {
  const rows = pins
    .map(
      (pin) => `
        <div class="border-line/20 border-b py-1 last:border-b-0">
          <div class="font-display text-caption text-ink font-bold">${escapeHtml(pin.city ?? pin.name)}</div>
          <div class="text-caption text-ink-soft">${escapeHtml(pin.place ?? "")}</div>
          <div class="text-caption text-ink-muted">${escapeHtml(pin.organizer_name ?? "-")} · ${escapeHtml(pin.state)}</div>
        </div>`,
    )
    .join("");

  return `<div class="flex min-w-44 flex-col">${rows}</div>`;
}

// Pins that overlap hide each other, which is exactly the case the map exists to
// show - several people applying for one metro. Grouping has to be done in
// screen space and redone on every zoom: at world zoom a whole metro is one
// pixel, and zoomed in the same cities are streets apart.
function clusterAtZoom(map: L.Map, pins: MapPin[]) {
  const points = pins.map((pin) => ({
    pin,
    point: map.latLngToLayerPoint([pin.latitude, pin.longitude]),
  }));

  const groups: (typeof points)[] = [];

  for (const entry of points) {
    const near = groups.find((group) =>
      group.some((member) => member.point.distanceTo(entry.point) <= MERGE_PX),
    );
    if (near) near.push(entry);
    else groups.push([entry]);
  }

  return groups.map((group) => ({
    pins: group.map((g) => g.pin),
    center: L.latLng(
      group.reduce((sum, g) => sum + g.pin.latitude, 0) / group.length,
      group.reduce((sum, g) => sum + g.pin.longitude, 0) / group.length,
    ),
  }));
}

export default function EventMap({ pins }: { pins: MapPin[] }) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);

  const draw = useCallback(() => {
    const instance = map.current;
    if (!instance || !layer.current) return;

    layer.current.clearLayers();

    for (const group of clusterAtZoom(instance, pins)) {
      L.marker(group.center, {
        icon: pinIcon(group.pins),
        title: group.pins.map((p) => p.city ?? p.name).join(", "),
      })
        .bindPopup(popupHtml(group.pins))
        .addTo(layer.current);
    }
  }, [pins]);

  useEffect(() => {
    if (!container.current || map.current) return;

    const instance = L.map(container.current, { scrollWheelZoom: false });
    map.current = instance;
    layer.current = L.layerGroup().addTo(instance);

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: "© OpenStreetMap",
    }).addTo(instance);

    return () => {
      instance.remove();
      map.current = null;
      layer.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;

    if (pins.length > 0) {
      instance.fitBounds(
        L.latLngBounds(
          pins.map((p) => [p.latitude, p.longitude] as [number, number]),
        ),
        { padding: [40, 40], maxZoom: 9 },
      );
    } else {
      instance.setView([20, 0], 2);
    }

    draw();
    instance.on("zoomend", draw);

    return () => {
      instance.off("zoomend", draw);
    };
  }, [pins, draw]);

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={container}
        className="border-line bg-surface-plain h-72 w-full border-2 lg:h-96"
        role="img"
        aria-label={`Map of ${pins.length} events`}
      />
      <div className="flex flex-wrap items-center gap-2">
        {/* Derived from what's actually plotted - the caller decides which
            states go on the map, so a fixed legend would describe pins that
            aren't there. */}
        {pins.some((p) => p.state === "submitted") && (
          <Badge tone="warning">awaiting review</Badge>
        )}
        {pins.some((p) => p.state === "approved") && (
          <Badge tone="success">approved</Badge>
        )}
        <span className="text-caption text-ink-soft">
          Pins come from the city each organizer typed. A numbered pin is more
          than one in the same place - zoom in to split them.
        </span>
      </div>
    </div>
  );
}
